import { Controller } from "@hotwired/stimulus";
import { morphTurboFrameElements } from "@hotwired/turbo";

export default class extends Controller {
  static values = {
    state: String,
    url: String,
    token: String,
    initialVersion: String,
  };

  static targets = ["indicator"];

  connect() {
    this.isRefreshing = false;
    const state = this.stateValue || "up_to_date";
    if (state === "needs_updating") {
      this.showIndicator();
      this.directCall();
    } else if (state === "updating") {
      this.showIndicator();
      this.pollUntilFresh();
    }
  }

  showIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.remove("d-none");
    }
  }

  hideIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.add("d-none");
    }
  }

  async directCall() {
    if (this.isRefreshing) {
      return;
    }

    if (!this.urlValue || !this.isAbsoluteUrl(this.urlValue)) {
      this.pollUntilFresh();
      return;
    }

    this.isRefreshing = true;

    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.tokenValue}`,
          Accept: "application/json",
        },
        mode: "cors",
        credentials: "omit",
      });

      if (resp.status === 200) {
        await this.reloadFrame();
        this.hideIndicator();
        this.isRefreshing = false;
      } else if (
        resp.status === 202 ||
        resp.status === 409 ||
        resp.status === 423
      ) {
        this.isRefreshing = false;
        this.pollUntilFresh();
      } else {
        const statusText = resp.statusText || 'Unknown';
        console.error("[price_refresh] Price refresh failed:", resp.status, statusText);
        this.hideIndicator();
        this.isRefreshing = false;
      }
    } catch (e) {
      console.error("[price_refresh] directCall error:", e, {
        controller: "price_refresh",
        action: "directCall",
      });
      this.isRefreshing = false;
      // Timeout/network error: fallback to polling briefly
      this.pollUntilFresh();
    }
  }

  async pollUntilFresh() {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    const initial = this.initialVersionValue;
    let attempts = 0;

    const maxAttempts = 60; // ~90s when interval is 1500ms
    const intervalMs = 1500;

    const poll = async () => {
      try {
        // Hit the freshness endpoint for a cheap check
        const f = await fetch(`${this.basePath()}/freshness`, {
          headers: { Accept: "application/json" },
        });
        if (f.ok) {
          const body = await f.json();
          if (body && typeof body === "object" && body.last_updated && body.last_updated !== initial) {
            await this.reloadFrame();
            this.hideIndicator();
            this.isRefreshing = false;
            return;
          }
        }
      } catch (e) {
        // Ignore and continue polling
        // Only capture error on first attempt to avoid spam.
        // If the issue persists, the max attempts timeout error will be captured.
        if (attempts === 0) {
          console.error("[price_refresh] pollUntilFresh error:", e, {
            controller: "price_refresh",
            action: "pollUntilFresh",
            attempts,
          });
        }
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(poll, intervalMs);
      } else {
        console.error("[price_refresh] Polling exceeded max attempts:", {
          controller: "price_refresh",
          action: "pollUntilFresh",
          maxAttempts,
        });
        this.hideIndicator();
        this.isRefreshing = false;
      }
    };

    poll();
  }

  async reloadFrame() {
    // Morph rather than replace the frame so any in-flight user input
    // (e.g. quantities the customer is typing while the refresh fires)
    // survives the price update. `frame.src = ...` would trigger Turbo's
    // FrameRenderer, which always replaces; calling Turbo's
    // morphTurboFrameElements directly diffs the new HTML against the
    // existing DOM and preserves matching elements by id.
    const frame = document.getElementById("price_matrix");
    if (!frame) {
      return;
    }

    try {
      // Send the Turbo-Frame header so ProductsController#show's
      // `turbo_frame_request?` branch fires and returns just the
      // price_matrix partial -- without it the controller falls through
      // to rendering the entire product page, which we'd then discard
      // after extracting the frame.
      const resp = await fetch(`${this.basePath()}?only=price_matrix`, {
        headers: {
          Accept: "text/html",
          "Turbo-Frame": "price_matrix",
        },
        credentials: "same-origin",
      });
      if (!resp.ok) {
        return;
      }
      const doc = new DOMParser().parseFromString(await resp.text(), "text/html");
      const newFrame = doc.getElementById("price_matrix");
      if (newFrame) {
        morphTurboFrameElements(frame, newFrame);
      }
    } catch (e) {
      console.error("[price_refresh] reloadFrame error:", e, {
        controller: "price_refresh",
        action: "reloadFrame",
      });
    }
  }

  isAbsoluteUrl(value) {
    try {
      const u = new URL(value);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch (e) {
      return false;
    }
  }

  basePath() {
    return window.location.pathname;
  }
}
